// src/components/alerts/AlertList.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { format } from 'date-fns';

const AlertList = ({ limit, refreshKey, onAlertsUpdated, isAdmin = false }) => {
  const { t } = useTranslation();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'
  const alertsEndpoint = isAdmin ? '/api/users/alerts' : '/api/journalist/alerts';
  const isLoadingRef = useRef(false);
  const timeoutRef = useRef(null);
  const lastLoadTimeRef = useRef(0);
  const DEBOUNCE_MS = 3000; // Minimum 3 seconds between calls

  const loadAlerts = useCallback(async (force = false) => {
    const now = Date.now();
    // Debounce: don't call if we're already loading or if called too recently
    if (!force && (isLoadingRef.current || (now - lastLoadTimeRef.current < DEBOUNCE_MS))) {
      return;
    }

    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce the actual API call
    timeoutRef.current = setTimeout(async () => {
      try {
        isLoadingRef.current = true;
        lastLoadTimeRef.current = Date.now();
        setLoading(true);
        // Clear alerts immediately when starting new load to prevent showing stale data
        setAlerts([]);
        
        const params = new URLSearchParams();
        if (limit) params.append('limit', limit);
        // For unread: is_read = false, for read: is_read = true
        if (filter === 'unread') {
          params.append('is_read', 'false');
        } else if (filter === 'read') {
          params.append('is_read', 'true');
        }
        // For 'all', don't send is_read parameter

        console.log('[AlertList] Loading alerts with filter:', filter, 'params:', params.toString());
        const response = await api.get(`${alertsEndpoint}?${params.toString()}`);
        const receivedAlerts = response.data.alerts || [];
        console.log('[AlertList] Received', receivedAlerts.length, 'alerts.');
        console.log('[AlertList] Alert details:', receivedAlerts.map(a => ({ id: a.id?.substring(0, 8) || 'no-id', is_read: a.is_read, title: (a.title || '').substring(0, 30) })));
        setAlerts(receivedAlerts);
      } catch (error) {
        console.error('Failed to load alerts:', error);
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
      }
    }, force ? 0 : 500); // Immediate if forced, 500ms debounce otherwise
  }, [limit, filter, alertsEndpoint]);

  useEffect(() => {
    // Auto-load when filter or limit changes
    // Clear alerts first to prevent showing stale data
    setAlerts([]);
    loadAlerts(true);
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, filter]);

  // Separate effect for refreshKey to handle parent-triggered refreshes
  useEffect(() => {
    if (refreshKey > 0) {
      // Force reload when refreshKey changes (from parent component)
      loadAlerts(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const handleMarkAsRead = async (alertId) => {
    try {
      await api.patch(`${alertsEndpoint}/${alertId}/read`);
      // Optimistically update the local state
      setAlerts(prevAlerts => 
        prevAlerts.map(alert => 
          alert.id === alertId ? { ...alert, is_read: true } : alert
        )
      );
      // Force reload to get fresh data and update counts
      await loadAlerts(true);
      // Notify parent to update stats
      if (onAlertsUpdated) {
        onAlertsUpdated();
      }
    } catch (error) {
      console.error('Failed to mark alert as read:', error);
      // Reload on error to ensure consistency
      await loadAlerts(true);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.post(`${alertsEndpoint}/read-all`);
      // If viewing unread alerts, switch to "all" since there are no unread alerts left
      if (filter === 'unread') {
        setFilter('all');
      }
      // Force reload to get fresh data
      await loadAlerts(true);
      // Notify parent to update stats
      if (onAlertsUpdated) {
        onAlertsUpdated();
      }
    } catch (error) {
      console.error('Failed to mark all alerts as read:', error);
      // Reload on error to ensure consistency
      await loadAlerts(true);
    }
  };

  const getAlertIcon = (alertType) => {
    const icons = {
      pattern_detected: (
        <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      new_investigation: (
        <svg className="w-6 h-6 text-shayek-primary dark:text-shayek-red-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      report_verified: (
        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      similar_reports: (
        <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
    };
    return icons[alertType] || (
      <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-2 text-sm text-gray-600">{t('common.loading')}</p>
      </div>
    );
  }

  // Calculate unread count from all alerts, not just filtered ones
  // We need to fetch all alerts to get accurate count, but for now use filtered count
  // The parent component (JournalistDashboard) should provide the total unread count
  const unreadCount = alerts.filter(a => !a.is_read).length;
  
  // Show empty state only if we actually have no alerts (not just filtered out)
  const showEmptyState = alerts.length === 0 && !loading;

  return (
    <div className="space-y-4">
      {!limit && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex gap-2">
            <button
              onClick={() => {
                setFilter('all');
                // Clear alerts immediately and force reload
                setAlerts([]);
                loadAlerts(true);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-primary-600 dark:bg-shayek-primary text-white'
                  : 'bg-gray-200 dark:bg-shayek-dark-200 text-gray-700 dark:text-shayek-dark-700 hover:bg-gray-300 dark:hover:bg-shayek-dark-300'
              }`}
            >
              {t('journalist.alerts.filters.all')}
            </button>
            <button
              onClick={() => {
                setFilter('unread');
                // Clear alerts immediately and force reload
                setAlerts([]);
                loadAlerts(true);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors relative ${
                filter === 'unread'
                  ? 'bg-primary-600 dark:bg-shayek-primary text-white'
                  : 'bg-gray-200 dark:bg-shayek-dark-200 text-gray-700 dark:text-shayek-dark-700 hover:bg-gray-300 dark:hover:bg-shayek-dark-300'
              }`}
            >
              {t('journalist.alerts.filters.unread')}
              {unreadCount > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            <button
              onClick={() => {
                setFilter('read');
                // Clear alerts immediately and force reload
                setAlerts([]);
                loadAlerts(true);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'read'
                  ? 'bg-primary-600 dark:bg-shayek-primary text-white'
                  : 'bg-gray-200 dark:bg-shayek-dark-200 text-gray-700 dark:text-shayek-dark-700 hover:bg-gray-300 dark:hover:bg-shayek-dark-300'
              }`}
            >
              {t('journalist.alerts.filters.read')}
            </button>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="px-4 py-2 bg-gray-200 dark:bg-shayek-dark-200 text-gray-700 dark:text-shayek-dark-700 rounded-lg hover:bg-gray-300 dark:hover:bg-shayek-dark-300 transition-colors text-sm"
            >
              {t('journalist.alerts.markAllRead')}
            </button>
          )}
        </div>
      )}

      {showEmptyState ? (
        <div className="text-center py-8 text-gray-500">
          <p>{t('journalist.alerts.empty')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map((alert) => (
          <div
            key={alert.id}
            className={`bg-white dark:bg-shayek-dark-100 border rounded-lg p-4 transition-colors ${
              alert.is_read
                ? 'border-gray-200 dark:border-shayek-dark-200 bg-gray-50 dark:bg-shayek-dark-50'
                : 'border-primary-200 dark:border-shayek-primary/30 bg-primary-50 dark:bg-shayek-primary/10'
            }`}
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-1">
                {getAlertIcon(alert.alert_type)}
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className={`font-semibold mb-1 ${alert.is_read ? 'text-gray-700 dark:text-shayek-dark-600' : 'text-gray-900 dark:text-shayek-dark-900'}`}>
                      {alert.title}
                    </h3>
                    <p className={`text-sm mb-2 ${alert.is_read ? 'text-gray-500 dark:text-shayek-dark-500' : 'text-gray-700 dark:text-shayek-dark-700'}`}>
                      {alert.message}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-shayek-dark-500">
                      <span>{format(new Date(alert.created_at), 'MMM d, yyyy HH:mm')}</span>
                      <span className="capitalize">{alert.alert_type.replace('_', ' ')}</span>
                      {isAdmin && alert.user_email && (
                        <span className="text-gray-400 dark:text-shayek-dark-400">• {alert.user_email} ({alert.user_role})</span>
                      )}
                    </div>
                  </div>
                  {!alert.is_read && (
                    <button
                      onClick={() => handleMarkAsRead(alert.id)}
                      className="ml-4 px-3 py-1 bg-primary-600 dark:bg-shayek-primary text-white rounded-lg hover:bg-primary-700 dark:hover:bg-shayek-primary-600 transition-colors text-sm"
                    >
                      {t('journalist.alerts.markRead')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        </div>
      )}
    </div>
  );
};

export default AlertList;

